/**
 * whatsappHandoff
 *
 * After every form submission the site opens a WhatsApp chat to the PickQuick
 * lead line with the submitted details pre-filled and URL-encoded. This runs
 * IN ADDITION to the backend POST, never instead of it: the structured lead
 * record (with its `lead_type`, `source`, `platforms[]`, `help_with[]` tags)
 * must always land in Mongo so Ferzin / Anuj / Swapnil can triage.
 *
 * Usage:
 *   await fetch("/api/leads", ...);        // backend record with structured tags
 *   openWhatsAppHandoff({                   // human handoff, opens in new tab
 *     source: "consulting-form",
 *     fields: {
 *       "Brand": "Acme Foods",
 *       "Work email": "a@b.com",
 *       "Platforms": "Blinkit, Zepto",
 *       ...
 *     },
 *   });
 */

export const WHATSAPP_NUMBER = "918733085863";

const APP_ORIGIN =
  typeof window !== "undefined" && window.location ? window.location.origin : "";

const formatSection = (label, value) => {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return `${label}: ${value.join(", ")}`;
  }
  const str = String(value).trim();
  if (!str) return null;
  return `${label}: ${str}`;
};

/**
 * Build the readable WhatsApp message body. Keeps line breaks so the
 * incoming message renders cleanly on the operator's phone.
 */
export const buildWhatsAppMessage = ({
  title = "New PickQuick lead",
  source,
  pagePath,
  fields = {},
}) => {
  const lines = [];
  lines.push(`Hi PickQuick, ${title.toLowerCase()}.`);
  lines.push("");
  Object.entries(fields).forEach(([label, value]) => {
    const line = formatSection(label, value);
    if (line) lines.push(line);
  });
  lines.push("");
  if (source) lines.push(`Source: ${source}`);
  const path = pagePath || (typeof window !== "undefined" ? window.location.pathname : "");
  if (path) lines.push(`Page: ${APP_ORIGIN}${path}`);
  return lines.join("\n");
};

/**
 * Opens wa.me in a new tab with a URL-encoded pre-filled message. This is
 * a fire-and-forget helper: no return value.
 *
 * IMPORTANT: browsers pop-block `window.open` unless it is called
 * SYNCHRONOUSLY from a user-gesture event handler (click/submit). Callers
 * must invoke this BEFORE any `await`. Use `submitLeadWithWhatsApp()`
 * below when you also need to POST the record to the backend.
 */
export const openWhatsAppHandoff = ({ title, source, pagePath, fields } = {}) => {
  if (typeof window === "undefined") return;
  const body = buildWhatsAppMessage({ title, source, pagePath, fields });
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(body)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Preferred submit orchestrator for every lead form on the site.
 *
 *   1. Opens the WhatsApp draft SYNCHRONOUSLY (before any await), so the
 *      new tab is created inside the click/submit user-gesture and never
 *      gets pop-blocked.
 *   2. Fires the backend POST silently underneath. If the POST fails, we
 *      log it but do NOT reverse the WhatsApp handoff. Losing a Mongo
 *      record is recoverable; losing the lead is not.
 *
 * Returns the fetch promise for callers that want to await success for
 * UI reasons (toast, form reset), but the WhatsApp draft is already open
 * by the time this returns.
 */
export const submitLeadWithWhatsApp = ({
  backendUrl,
  payload,
  whatsapp,
} = {}) => {
  // Step 1: fire WhatsApp draft synchronously.
  openWhatsAppHandoff(whatsapp || {});

  // Step 2: POST silently. Any failure is logged, never surfaced to
  // the visitor in a way that would block the handoff.
  const url = `${backendUrl || process.env.REACT_APP_BACKEND_URL}/api/leads`;
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.error("[lead-post] backend POST failed, WhatsApp handoff already fired:", err);
    return null;
  });
};
